import {AdminAlert} from '../../components/AdminAlert/AdminAlert'
import axios from 'axios'
import React, {useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import LoadingOverlay from 'react-loading-overlay'
import {
  AdminDataTable,
  AdminStatusBadge,
  AdminTableAction,
  AdminTableActions,
  AdminTableSearch,
} from '../../components/AdminDataTable/AdminDataTable'
import {PasswordSecurityFields, validatePasswordFields, passwordPayload} from '../../components/ChangePasswordForm/ChangePasswordForm'
import '../Distribuidores/Distribuidores.css'
import '../Vendedores/Vendedores.css'

const defaultRoles = ['Admin', 'Gerente Comercial', 'Gestor de Catálogo', 'Diseño']
const defaultStatuses = ['Activo', 'Inactivo']
const emptyForm = {
  nombre: '',
  apellido: '',
  email: '',
  rol: 'Admin',
  estatus: 'Activo',
  password: '',
  confirmPassword: '',
}

function Administradores() {
  const API_URL = process.env.REACT_APP_API_URL
  const [administrators, setAdministrators] = useState([])
  const [roles, setRoles] = useState(defaultRoles)
  const [statuses, setStatuses] = useState(defaultStatuses)
  const [currentAdminId, setCurrentAdminId] = useState(null)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [message, setMessage] = useState(null)
  const [formError, setFormError] = useState('')

  const loadAdministrators = async () => {
    setLoading(true)
    try {
      const {data} = await axios.get(`${API_URL}/administradores`)
      setAdministrators(data.data || [])
      setRoles(data.roles?.length ? data.roles : defaultRoles)
      setStatuses(data.statuses?.length ? data.statuses : defaultStatuses)
      setCurrentAdminId(data.currentAdminId || null)
    } catch (error) {
      setMessage({
        type: 'danger',
        text: error.response?.data?.message || 'No fue posible consultar los administradores.',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAdministrators()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('es-MX')
    if (!term) return administrators
    return administrators.filter((administrator) =>
      `${administrator.nombre} ${administrator.apellido} ${administrator.email} ${administrator.rol} ${administrator.estatus}`
        .toLocaleLowerCase('es-MX')
        .includes(term)
    )
  }, [administrators, search])

  const openCreate = () => {
    setEditing(null)
    setForm({...emptyForm, rol: roles[0] || 'Admin', estatus: statuses[0] || 'Activo'})
    setFormError('')
    setShowModal(true)
  }

  const openEdit = (administrator) => {
    setEditing(administrator)
    setForm({
      nombre: administrator.nombre || '',
      apellido: administrator.apellido || '',
      email: administrator.email || '',
      rol: administrator.rol || roles[0] || 'Admin',
      estatus: administrator.estatus || statuses[0] || 'Activo',
      password: '',
      confirmPassword: '',
    })
    setFormError('')
    setShowModal(true)
  }

  const validate = () => {
    if (!form.nombre.trim()) return 'El nombre es obligatorio.'
    if (!form.apellido.trim()) return 'El apellido es obligatorio.'
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return 'Ingresa un correo electrónico válido.'
    if (!roles.includes(form.rol)) return 'El rol seleccionado no es válido.'
    if (!statuses.includes(form.estatus)) return 'El estatus seleccionado no es válido.'
    return validatePasswordFields(form.password, form.confirmPassword, !editing)
  }

  const save = async (event) => {
    event.preventDefault()
    const validationError = validate()
    if (validationError) {
      setFormError(validationError)
      return
    }

    setLoading(true)
    setFormError('')
    try {
      const {password, confirmPassword, ...generalFields} = form
      const payload = {
        ...generalFields,
        nombre: form.nombre.trim(),
        apellido: form.apellido.trim(),
        email: form.email.trim(),
      }
      if (editing) {
        await axios.put(`${API_URL}/administradores/${editing.id}`, {...payload, ...passwordPayload(password, confirmPassword)})
      } else await axios.post(`${API_URL}/administradores`, {...payload, password, confirmPassword})
      await loadAdministrators()
      setShowModal(false)
      setForm(emptyForm)
      setMessage({
        type: 'success',
        text: editing
          ? 'Usuario interno actualizado correctamente.'
          : 'Usuario interno creado correctamente.',
      })
    } catch (error) {
      console.error('No fue posible guardar el usuario interno.', error)
      setFormError(error.response?.data?.message || 'No fue posible guardar el usuario interno.')
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {name: 'Nombre', selector: (row) => row.nombre, sortable: true},
    {name: 'Apellido', selector: (row) => row.apellido, sortable: true},
    {name: 'Email', selector: (row) => row.email, sortable: true, grow: 1.5, wrap: true},
    {name: 'Rol', selector: (row) => row.rol, sortable: true},
    {name: 'Fecha de alta', selector: (row) => row.fechaAlta || '', sortable: true, cell: (row) => row.fechaAlta ? new Intl.DateTimeFormat('es-MX').format(new Date(row.fechaAlta.replace(' ', 'T'))) : '—'},
    {
      name: 'Estatus',
      selector: (row) => row.estatus,
      sortable: true,
      cell: (row) => <AdminStatusBadge status={row.estatus} />,
    },
    {
      name: 'Acciones',
      right: true,
      width: '100px',
      cell: (row) => (
        <AdminTableActions>
          <AdminTableAction
            variant='edit'
            title='Editar'
            icon='fas fa-pen'
            onClick={() => openEdit(row)}
          />
        </AdminTableActions>
      ),
    },
  ]

  const editingOwnAccount = editing && Number(editing.id) === Number(currentAdminId)

  return (
    <LoadingOverlay active={loading} spinner text='Cargando...'>
      <div className='sellers-page'>
        <AdminAlert message={message} onClose={setMessage} />
        <div className='card card-custom shadow admin-list-card sellers-card'>
          <div className='card-header sellers-header'>
            <div>
              <h1>Usuarios internos</h1>
              <p>Administra las cuentas internas y sus permisos de acceso al sistema.</p>
            </div>
            <button type='button' className='btn btn-primary sellers-add' onClick={openCreate}>
              <i className='fas fa-plus'></i> Nuevo usuario interno
            </button>
          </div>
          <div className='card-body'>
            <AdminTableSearch
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              ariaLabel='Buscar usuarios internos'
            />
            <AdminDataTable
              columns={columns}
              data={filtered}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              noDataComponent='No se encontraron usuarios internos.'
            />
          </div>
        </div>

        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          centered
          dialogClassName='seller-modal'
        >
          <form onSubmit={save}>
            <Modal.Header closeButton>
              <Modal.Title>{editing ? 'Editar usuario interno' : 'Nuevo usuario interno'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {formError && <div className='alert alert-danger seller-form-error'>{formError}</div>}
              <section className='distributor-section seller-modal-section'>
                <h3>Información general del usuario interno</h3>
                <div className='distributor-grid seller-general-grid'>
                  <label className='distributor-field'>
                    <span>Nombre</span>
                    <input
                      className='form-control'
                      maxLength='100'
                      required
                      value={form.nombre}
                      onChange={(event) => setForm({...form, nombre: event.target.value})}
                    />
                  </label>
                  <label className='distributor-field'>
                    <span>Apellido</span>
                    <input
                      className='form-control'
                      maxLength='100'
                      required
                      value={form.apellido}
                      onChange={(event) => setForm({...form, apellido: event.target.value})}
                    />
                  </label>
                  <label className='distributor-field seller-email-field'>
                    <span>Email</span>
                    <input
                      className='form-control'
                      type='email'
                      maxLength='254'
                      required
                      value={form.email}
                      onChange={(event) => setForm({...form, email: event.target.value})}
                    />
                  </label>
                  <label className='distributor-field'>
                    <span>Rol</span>
                    <select
                      className='form-select'
                      value={form.rol}
                      disabled={editingOwnAccount}
                      onChange={(event) => setForm({...form, rol: event.target.value})}
                    >
                      {roles.map((role) => <option key={role}>{role}</option>)}
                    </select>
                  </label>
                  <label className='distributor-field'>
                    <span>Estatus</span>
                    <select
                      className='form-select'
                      value={form.estatus}
                      disabled={editingOwnAccount}
                      onChange={(event) => setForm({...form, estatus: event.target.value})}
                    >
                      {statuses.map((status) => <option key={status}>{status}</option>)}
                    </select>
                  </label>
                </div>
                {editingOwnAccount && (
                  <p className='text-muted mt-3 mb-0'>Tu propio rol y estatus permanecen protegidos.</p>
                )}
              </section>
              <section className='distributor-section seller-modal-section'>
                <h3>Contraseña y seguridad</h3>
                <div className='password-security-fields'>
                  <PasswordSecurityFields required={!editing} newPassword={form.password} confirmPassword={form.confirmPassword} onNewPasswordChange={(password) => {setForm({...form, password}); setFormError('')}} onConfirmPasswordChange={(confirmPassword) => {setForm({...form, confirmPassword}); setFormError('')}} />
                </div>
              </section>
              <div className='distributor-actions seller-modal-actions'>
                <button type='submit' className='btn btn-primary'>
                  {editing ? 'Guardar cambios' : 'Guardar'}
                </button>
                <button type='button' className='btn btn-secondary' onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
              </div>
            </Modal.Body>
          </form>
        </Modal>
      </div>
    </LoadingOverlay>
  )
}

export {Administradores}
